import { zodResolver } from "@hookform/resolvers/zod";
import { useGetAccount } from "apollo/hooks/queries";
import { Result } from "apollo/utils";
import { capitaliseFirst } from "core/model/utils/strings";
import { Account } from "core/types";
import {
  ERROR_COLOR,
  SUCCESS_COLOR,
  TEXT_DISABLED_COLOR,
} from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { margin, padding } from "ds_legacy/materials/metrics";
import {
  Caption,
  FONT_SIZE_16,
  Subheading,
  Title,
} from "ds_legacy/materials/typography";
import { Button, ControlledInput, Form } from "ds/ui";
import { useAcpNavigationHandlers } from "dsl/hooks";
import { CheckIcon, XCircleIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { SealdDebugSearchFormValues, sealdDebugSearchSchema } from "./schema";
import { DetailsTable, DetailsTableRow } from "./shared";

export default function AccountPage() {
  const [search, setSearch] = useSearchParams();
  const accountIdQueryparam = search.get("account_id");
  const [accountId, setAccountId] = useState<number | null>(null);

  const [queryProgress, account] = useGetAccount(accountId ?? -1);

  const methods = useForm<SealdDebugSearchFormValues>({
    defaultValues: { entity_id: accountIdQueryparam ?? "" },
    resolver: zodResolver(sealdDebugSearchSchema),
  });

  useEffect(() => {
    if (accountIdQueryparam) {
      setAccountId(Number(accountIdQueryparam));
    } else {
      setAccountId(null);
    }
    methods.reset({ entity_id: accountIdQueryparam ?? "" });
  }, [accountIdQueryparam, methods]);

  const onSubmit = methods.handleSubmit(({ entity_id }) => {
    setAccountId(Number(entity_id));
    search.set("account_id", entity_id);
    setSearch(search);
  });

  return (
    <>
      <FormProvider {...methods}>
        <Form onSubmit={onSubmit}>
          <div className="flex flex-row items-end gap-2">
            <ControlledInput label="Account ID" name="entity_id" />
            <Button id="search" type="submit" variant="ghost">
              Search
            </Button>
          </div>
        </Form>
      </FormProvider>
      <Result
        data={account}
        ErrorComponent={<Caption>Could not get account {accountId}</Caption>}
        id="acp-seald-getaccount"
        queryProgress={queryProgress}
      >
        {(loadedAccount) => <AccountDetails account={loadedAccount} />}
      </Result>
    </>
  );
}

function AccountDetails({ account }: { account: Account }) {
  const {
    seald_database_key,
    seald_registered,
    seald_test_identity,
    seald_two_man_rule_key,
    seald_user_license_token,
  } = account;
  const sealdProps = {
    seald_database_key,
    seald_registered,
    seald_test_identity,
    seald_two_man_rule_key,
    seald_user_license_token,
  };
  if (!seald_test_identity) delete sealdProps.seald_test_identity;

  return (
    <>
      <Title>
        Account {account.id} - {account.email}
      </Title>
      <DetailsTable>
        <tbody>
          <DetailsTableRow>
            <td
              style={{
                padding: padding(0),
              }}
            >
              <Subheading>encryption activated for account</Subheading>
            </td>
            <td
              style={{
                padding: padding(0, 4),
              }}
            >
              {seald_registered || seald_test_identity ? (
                <CheckIcon
                  size={FONT_SIZE_16}
                  style={{ color: SUCCESS_COLOR }}
                />
              ) : (
                <XCircleIcon
                  size={FONT_SIZE_16}
                  style={{ color: ERROR_COLOR }}
                />
              )}
            </td>
            <td
              style={{
                padding: padding(2, 4),
              }}
            >
              <VerticalLayout>
                {Object.entries(sealdProps).map(([key, value]) => (
                  <Caption key={key} color={TEXT_DISABLED_COLOR}>
                    {key}: {value ? "true" : "false"}
                  </Caption>
                ))}
              </VerticalLayout>
            </td>
          </DetailsTableRow>
        </tbody>
      </DetailsTable>
      <HorizontalLayout>
        <VerticalLayout margin={margin(0, 6)} style={{ flex: 1 }}>
          <RolesColumn
            entityRoles={account.roles?.careseeker_roles
              ?.clone()
              .sort((a, b) => (a.careseeker?.id ?? 0) - (b.careseeker?.id ?? 0))
              .map((role) => ({
                id: role.careseeker?.id ?? -1,
                name: role.careseeker?.name ?? "",
              }))}
            type="sender"
          />
        </VerticalLayout>
        <VerticalLayout margin={margin(0, 6)} style={{ flex: 1 }}>
          <RolesColumn
            entityRoles={account.roles?.careprovider_roles
              ?.clone()
              .sort(
                (a, b) => (a.careprovider?.id ?? 0) - (b.careprovider?.id ?? 0),
              )
              .map((role) => ({
                id: role.careprovider?.id ?? -1,
                name: role.careprovider?.name ?? "",
              }))}
            type="receiver"
          />
        </VerticalLayout>
      </HorizontalLayout>
    </>
  );
}

function RolesColumn({
  entityRoles,
  type,
}: {
  entityRoles: Array<{ id: number; name: string }> | undefined;
  type: "receiver" | "sender";
}) {
  const navigationHandlers = useAcpNavigationHandlers();
  return (
    <>
      <Title>{capitaliseFirst(type)} roles</Title>
      {entityRoles?.length ? (
        <DetailsTable>
          <tbody>
            {entityRoles.map(({ id, name }) => (
              <DetailsTableRow
                key={`role-${type}-${id}`}
                onClick={() => {
                  const page =
                    type === "sender"
                      ? `sender?careseeker_id=${id}`
                      : `receiver?careprovider_id=${id}`;
                  navigationHandlers.goToSealdDebug(page);
                }}
              >
                <td
                  style={{
                    padding: padding(0.5, 1),
                  }}
                >
                  <Caption whiteSpace="normal">
                    #{id} - {name}
                  </Caption>
                </td>
              </DetailsTableRow>
            ))}
          </tbody>
        </DetailsTable>
      ) : (
        <Caption>none</Caption>
      )}
    </>
  );
}
